import React, { useState, useEffect, useCallback } from 'react';
import { Table, Avatar, Tag, Spin, Alert, Button } from 'antd';
import { UserOutlined, CrownOutlined, CheckCircleOutlined, ThunderboltOutlined, RotateRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { db } from '../Firebase';
import { collection, getDocs } from 'firebase/firestore';

import Swal from 'sweetalert2';
import './Leaderboard.css';

// Component สำหรับหน้ากระดานผู้นำ
function Leaderboard({ userData }) { // รับ userData จาก App.js
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null); // ใช้สำหรับ Firebase Auth UID
  const [isAuthReady, setIsAuthReady] = useState(false); // สถานะพร้อมใช้งาน Auth
  const [error, setError] = useState(null);
  const [hasHistoryData, setHasHistoryData] = useState(false); // ตรวจสอบว่ามีข้อมูล history หรือไม่

  // ดึง __app_id จาก global scope (สำหรับ Canvas environment)
  const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id';

  // Effect สำหรับจัดการสถานะ Firebase Authentication
  // Effect สำหรับจัดการสถานะ Firebase Authentication - REMOVED
  // แอพใช้ Custom Auth (Firestore P1) ไม่จำเป็นต้องใช้ Firebase Auth SDK
  useEffect(() => {
    setIsAuthReady(true); // ตั้งค่าให้พร้อมใช้งานทันที
  }, []);

  // ฟังก์ชันสำหรับดึงข้อมูล Leaderboard จาก Firestore
  const fetchLeaderboardData = useCallback(async () => {
    // ไม่ดึงข้อมูลหาก Firestore instance ไม่พร้อมหรือ Auth ยังไม่พร้อม
    if (!db || !isAuthReady) {
      return;
    }
    setLoading(true); // เริ่มโหลดข้อมูล
    setError(null); // ล้างข้อผิดพลาดก่อนหน้า
    try {
      // ดึงข้อมูลจาก Collection ตามที่ user login เข้ามา
      const collectionName = userData?.collection || "Competition-Training";
      const usersCollectionRef = collection(db, collectionName); 
      const querySnapshot = await getDocs(usersCollectionRef);
      const fetchedUsers = [];
      querySnapshot.forEach((document) => {
        const data = document.data();
        
        // ตรวจสอบว่าข้อมูลมี username, level, score และเป็นประเภทที่ถูกต้อง
        // และต้องไม่ถูกซ่อน (hide != true)
        if (data.hide === true) {
          return;
        }

        if (data.username && typeof data.score === 'number') {
          // สร้างชื่อเต็มจาก name และ surname
          const fullName = data.name && data.surname 
            ? `${data.name} ${data.surname}` 
            : data.name || data.surname || data.username; // fallback ไปที่ name, surname หรือ username
          
          // คำนวณ Step จากผลบวกของ Show-Silver + Show-Gold + Show-Diamond
          const showSilver = data['show-silver'] || 0;
          const showGold = data['show-gold'] || 0;
          const showDiamond = data['show-diamond'] || 0;
          
          // คำนวณ Step จากผลบวกของ step1-total ถึง step5-total
          // Calculate Step X totals
          const step1 = data['step1'] || 0;
          const step2 = data['step2'] || 0;
          const step3 = data['step3'] || 0;
          const step4 = data['step4'] || 0;
          const step5 = data['step5'] || 0;
          const step6 = data['step6'] || 0;

          const step1Total = data['step1-total'] || 0;
          const step2Total = data['step2-total'] || 0;
          const step3Total = data['step3-total'] || 0;
          const step4Total = data['step4-total'] || 0;
          const step5Total = data['step5-total'] || 0;
          const step6Total = data['step6-total'] || 0;

          // Calculate Accuracy
          const totalCorrect = step1 + step2 + step3 + step4 + step5 + step6;
          const totalPlayed = step1Total + step2Total + step3Total + step4Total + step5Total + step6Total;
          const accuracy = totalPlayed > 0 ? Math.floor((totalCorrect / totalPlayed) * 100) : 0;

          // Calculate Speed
          const step1Speed = data['step1-speed'] || 0;
          const step2Speed = data['step2-speed'] || 0;
          const step3Speed = data['step3-speed'] || 0;
          const step4Speed = data['step4-speed'] || 0;
          const step5Speed = data['step5-speed'] || 0;
          const step6Speed = data['step6-speed'] || 0;
          const totalSpeedSum = step1Speed + step2Speed + step3Speed + step4Speed + step5Speed + step6Speed;

          // Calculate count of non-zero speed steps (where total > 0)
          let speedCount = 0;
          if (step1Speed > 0 && step1Total > 0) speedCount++;
          if (step2Speed > 0 && step2Total > 0) speedCount++;
          if (step3Speed > 0 && step3Total > 0) speedCount++;
          if (step4Speed > 0 && step4Total > 0) speedCount++;
          if (step5Speed > 0 && step5Total > 0) speedCount++;
          if (step6Speed > 0 && step6Total > 0) speedCount++;

          const speed = speedCount > 0 ? Math.floor(totalSpeedSum / speedCount) : 0;

          const totalSteps = totalPlayed; // Use totalPlayed as level/total steps
          
          
          
          fetchedUsers.push({
            id: document.id,
            username: data.username, // Add username field
            name: fullName,
            level: totalSteps, // ใช้ totalSteps แทน data.level
            score: data.score,
            accuracy: accuracy,
            speed: speed,
            showSilver: showSilver,
            showGold: showGold,
            showDiamond: showDiamond,
            stats: {
              step1: { correct: step1, total: step1Total, speed: step1Speed },
              step2: { correct: step2, total: step2Total, speed: step2Speed },
              step3: { correct: step3, total: step3Total, speed: step3Speed },
              step4: { correct: step4, total: step4Total, speed: step4Speed },
              step5: { correct: step5, total: step5Total, speed: step5Speed },
              step6: { correct: step6, total: step6Total, speed: step6Speed },
            },
            // เพิ่ม isCurrentUser เพื่อระบุแถวของผู้ใช้ปัจจุบัน
            isCurrentUser: userData && userData.username === data.username // ตรวจสอบ username
          });
        } else {
          console.warn("❌ Invalid user data structure:", data);
        }
      });

      if (fetchedUsers.length === 0) {
        setLeaderboardData([]); // ไม่มีข้อมูล
      } else {
        // จัดเรียงข้อมูล: Score มากที่สุดก่อน, หาก Score เท่ากัน Steps น้อยที่สุดก่อน
        fetchedUsers.sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return a.level - b.level; // เปลี่ยนเป็น Steps น้อยที่สุดก่อน (a.level - b.level)
        });

        // กำหนดอันดับ (Ranking) ให้กับแต่ละผู้ใช้
        const rankedData = fetchedUsers.map((user, index) => ({
          ...user,
          ranking: index + 1,
        }));

        setLeaderboardData(rankedData);
      }
    } catch (err) {
      // จัดการข้อผิดพลาดในการดึงข้อมูล
      if (err.code === 'permission-denied') {
        setError("Permission Denied: ตรวจสอบ Firestore Security Rules ของคุณ");
      } else {
        setError(`Error fetching data: ${err.message}. ตรวจสอบ Collection Path หรือการเชื่อมต่อ.`);
      }
      setLeaderboardData([]); // ล้างข้อมูลหากเกิดข้อผิดพลาด
    } finally {
      setLoading(false); // หยุดสถานะโหลด
    }
  }, [db, isAuthReady, userData]); // เพิ่ม userData ใน dependency array เพื่อให้ re-fetch เมื่อข้อมูลผู้ใช้เปลี่ยน

  // Effect สำหรับเรียก fetchLeaderboardData เมื่อ db หรือ Auth พร้อม
  useEffect(() => {
    if (db && isAuthReady) {
      fetchLeaderboardData();
    }
  }, [db, isAuthReady, fetchLeaderboardData]);

  // ฟังก์ชันสำหรับกลับไปยังหน้า Exercies
  const handleBackToExercies = () => {
    navigate('/');
  };

  // ฟังก์ชันสำหรับไปยังหน้า History
  const handleViewHistory = () => {
    navigate('/history');
  };

  // ฟังก์ชันสำหรับตรวจสอบว่าผู้ใช้มีข้อมูล history หรือไม่
  const checkHistoryData = useCallback(async () => {
    if (!userData?.username || !db) {
      setHasHistoryData(false);
      return;
    }

    try {
      const collectionName = userData?.collection || "Competition-Training";
      const historyCollectionRef = collection(db, collectionName, userData.username, "History");
      const querySnapshot = await getDocs(historyCollectionRef);
      setHasHistoryData(!querySnapshot.empty);
    } catch (error) {
      console.error("Error checking history data:", error);
      setHasHistoryData(false);
    }
  }, [userData?.username, db]);

  // Effect สำหรับตรวจสอบข้อมูล history เมื่อ userData เปลี่ยน
  useEffect(() => {
    if (userData?.username && db) {
      checkHistoryData();
    }
  }, [userData?.username, db, checkHistoryData]);

  // ฟังก์ชันแสดงสถิติ
  const handleShowStats = (record) => {
    const statsHtml = `
      <div class="stats-popup-container">
        <h3 class="stats-popup-title">${record.name} (@${record.username})</h3>
        <table class="stats-popup-table">
          <thead>
            <tr class="stats-popup-thead-tr">
              <th class="stats-popup-th">หัวข้อ</th>
              <th class="stats-popup-th">ข้อที่ทำถูก</th>
              <th class="stats-popup-th">ข้อที่ทำทั้งหมด</th>
              <th class="stats-popup-th">ความถูกต้อง</th>
              <th class="stats-popup-th">ความเร็วในการตอบ</th>
            </tr>
          </thead>
          <tbody>
            ${[
              { num: 1, name: "โจทย์บวกลบ" },
              { num: 2, name: "โจทย์บวกลบระคน" },
              { num: 3, name: "เกมส์บันเทิง" },
              { num: 4, name: "เกมส์โจทย์เชาว์" },
              { num: 5, name: "เกมส์จับคู่" },
              { num: 6, name: "เกมส์ศึกสูตรคูณ" }
            ].map(step => {
              const correct = record.stats[`step${step.num}`].correct;
              const total = record.stats[`step${step.num}`].total;
              const accuracy = total > 0 ? Math.floor((correct / total) * 100) : 0;
              return `
              <tr>
                <td class="stats-popup-td" style="text-align: left;"><strong>${step.name}</strong></td>
                <td class="stats-popup-td" style="text-align: center;">${correct}</td>
                <td class="stats-popup-td" style="text-align: center;">${total}</td>
                <td class="stats-popup-td" style="text-align: center;">${accuracy}%</td>
                <td class="stats-popup-td" style="text-align: center;">${Math.floor(record.stats[`step${step.num}`].speed)}%</td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
        <div class="stats-popup-footer">
             Accuracy: <span class="stats-accuracy">${record.accuracy}%</span> | 
             Avg Speed: <span class="stats-speed">${record.speed}%</span> |
             Total Score: ${record.score.toLocaleString()}
        </div>
      </div>
    `;

    Swal.fire({
      title: 'Player Statistics',
      html: statsHtml,
      width: 600,
      showCloseButton: true,
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6',
      customClass: {
        title: 'swal2-title-custom', // Custom class for title if needed
        popup: 'swal2-popup-custom'
      }
    });
  };

  // คอลัมน์สำหรับ Ant Design Table
  const columns = [
    {
      title: 'Rank',
      dataIndex: 'ranking',
      key: 'ranking',
      align: 'center',
      render: (text) => {
        // แสดงไอคอนมงกุฎเฉพาะอันดับ 1-3
        if (text === 1) {
          return (
            <div className="rank-cell gold">
              <CrownOutlined className="crown-icon" /> {text}
            </div>
          );
        } else if (text === 2) {
          return (
            <div className="rank-cell silver">
              <CrownOutlined className="crown-icon" /> {text}
            </div>
          );
        } else if (text === 3) {
          return (
            <div className="rank-cell bronze">
              <CrownOutlined className="crown-icon" /> {text}
            </div>
          );
        } else if (text >= 4 && text <= 5) {
          return (
            <div className={`rank-cell rank-${text}`}>
              {text}
            </div>
          );
        }
        return <div className="rank-cell">{text}</div>;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div className="name-cell">
          <Avatar icon={<UserOutlined />} className="avatar" />
          {text}
        </div>
      ),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      align: 'center',
      responsive: ['md'],
      render: (text) => <span className="username-cell" style={{ color: '#555' }}>{text}</span>,
    },
    {
      title: 'Performance',
      dataIndex: 'levels',
      key: 'levels',
      align: 'center',
      responsive: ['md'],
      render: (text, record) => (
        <div className="levels-cell" style={{ flexDirection: 'column', gap: '5px' }}>
          <div className="performance-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="perf-icon" style={{ color: '#52c41a' }}><CheckCircleOutlined /></span>
            <span className="perf-value">{record.accuracy}%</span>
          </div>
          <div className="performance-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
             <span className="perf-icon" style={{ color: '#faad14' }}><ThunderboltOutlined /></span>
             <span className="perf-value">{record.speed}%</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Total', // เปลี่ยนชื่อจาก "Performance" เป็น "Total"
      dataIndex: 'level',
      key: 'level',
      align: 'center',
      render: (text) => {
        let color = 'geekblue'; // สีเริ่มต้น
        if (text >= 200) {
          color = 'volcano'; // Steps สูงมาก (200+)
        } else if (text >= 150) {
          color = 'gold'; // Steps สูง (150-199)
        } else if (text >= 100) {
          color = 'lime'; // Steps ปานกลาง (100-149)
        } else if (text >= 50) {
          color = 'cyan'; // Steps เริ่มต้น (50-99)
        } else {
          color = 'blue'; // Steps น้อย (0-49)
        }
        return <Tag color={color} className="step-tag">{text}</Tag>;
      },
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (text) => (
        <span className="score-cell">
          {text.toLocaleString()} Points {/* แสดงผลคะแนนพร้อมข้อความ "Points" */}
        </span>
      ),
    },
    {
      title: 'Statistics',
      key: 'statistics',
      align: 'center',
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small"
          onClick={(e) => {
             e.stopPropagation(); // Prevent row click if any
             handleShowStats(record);
          }}
          className="view-stats-btn"
          style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8' }}
        >
          Statistics
        </Button>
      ),
    },
  ];

  return (
    <div className="leaderboard-container">


      <div className="leaderboard-content">
        <div className="leaderboard-header">

          {/* โลโก้อยู่ด้านขวา */}
          <div className="logo-container">
            <img 
              src="/Wisdom-Logo.png" 
              alt="Wisdom Logo" 
              className="logo-image1"
            />
          </div>

          {/* ข้อความอยู่ตรงกลาง */}
          <h1 className="leaderboard-title">
            Leaderboard
          </h1>
          
          {/* กลุ่มปุ่มสำหรับมือถือ */}
          <div className="button-group">
            <button onClick={handleBackToExercies} className="back-button">
              Back
            </button>        
            {hasHistoryData && (
              <button onClick={handleViewHistory} className="history-button">
                History
              </button>
            )}
          </div>

        </div>

        {error && (
          <Alert
            message="Title is deprecated, use message as description or title prop" // AntD < 5 uses message, but warning says use title. Let's try matching the warning.
            // Actually warning says: `message` is deprecated. Please use `title` instead.
            // But wait, AntD 4 Alert has message. AntD 5 might have changed it? 
            // Let's safe bet: use `message` as the main title if it's AntD 4, or `title` if AntD 5?
            // The user log shows: Warning: [antd: Alert] `message` is deprecated. Please use `title` instead.
            // So I will change message to title as requested.
            title="ข้อผิดพลาด"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <p>กำลังโหลด Leaderboard...</p>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={leaderboardData}
            rowKey="id"
            pagination={false}
            bordered
            rowClassName={(record) => {
              // เพิ่มคลาสสำหรับไฮไลต์ผู้ใช้ปัจจุบัน
              let className = '';
              if (record.ranking >= 1 && record.ranking <= 5) {
                className += ` rank-${record.ranking}-row`;
              }
              if (record.isCurrentUser) className += ' current-user-row';
              return className.trim();
            }}
            locale={{ emptyText: 'ไม่มีข้อมูลใน Leaderboard' }}
            className="leaderboard-table"
          />
        )}
        {userData?.username && (
          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.9em', color: '#666' }}>
              <p>Logged in as: {userData.username}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;

